// Spelled pitches. A pitch is a letter (step), an alteration and an octave.
// Octaves follow scientific pitch notation: C4 is middle C (MIDI 60).

export type Step = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
export type Alter = -2 | -1 | 0 | 1 | 2;

export interface Pitch {
  step: Step;
  alter: Alter;
  octave: number;
}

export const STEPS: readonly Step[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

export const STEP_SEMITONE: Readonly<Record<Step, number>> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/** Euclidean modulo: always returns a value in [0, m). */
export function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

export function stepIndex(step: Step): number {
  return STEPS.indexOf(step);
}

export function isStep(s: string): s is Step {
  return s.length === 1 && 'CDEFGAB'.includes(s);
}

export function pitch(step: Step, alter: number, octave: number): Pitch {
  if (alter < -2 || alter > 2 || !Number.isInteger(alter)) {
    throw new RangeError(`alter out of range: ${alter}`);
  }
  return { step, alter: alter as Alter, octave };
}

/** MIDI number of a spelled pitch (C4 = 60). */
export function midiOf(p: Pitch): number {
  return 12 * (p.octave + 1) + STEP_SEMITONE[p.step] + p.alter;
}

/** Absolute diatonic index: C0 = 0, D0 = 1 … C4 = 28. */
export function diatonicIndex(p: Pick<Pitch, 'step' | 'octave'>): number {
  return p.octave * 7 + stepIndex(p.step);
}

export function fromDiatonicIndex(d: number, alter: Alter = 0): Pitch {
  return { step: STEPS[mod(d, 7)], alter, octave: Math.floor(d / 7) };
}

export function pitchEquals(a: Pitch, b: Pitch): boolean {
  return a.step === b.step && a.alter === b.alter && a.octave === b.octave;
}

/** Order by sounding height, then by diatonic position (B#3 < C4 when enharmonic). */
export function comparePitch(a: Pitch, b: Pitch): number {
  return midiOf(a) - midiOf(b) || diatonicIndex(a) - diatonicIndex(b);
}

const ALTER_ASCII: Record<number, string> = { [-2]: 'bb', [-1]: 'b', 0: '', 1: '#', 2: 'x' };
const ALTER_UNICODE: Record<number, string> = { [-2]: '𝄫', [-1]: '♭', 0: '', 1: '♯', 2: '𝄪' };

export function alterSymbol(alter: number, unicode = true): string {
  return (unicode ? ALTER_UNICODE : ALTER_ASCII)[alter] ?? '';
}

/** "B♭5" (unicode) or "Bb5" (ascii). */
export function pitchName(p: Pitch, opts: { unicode?: boolean; octave?: boolean } = {}): string {
  const { unicode = true, octave = true } = opts;
  return p.step + alterSymbol(p.alter, unicode) + (octave ? String(p.octave) : '');
}

/** Pitch class name without octave: "B♭". */
export function pitchClassName(p: Pick<Pitch, 'step' | 'alter'>, unicode = true): string {
  return p.step + alterSymbol(p.alter, unicode);
}

/** Parse "C4", "Bb5", "F#3", "Ebb2", "Gx4", "B♭5". */
export function parsePitch(s: string): Pitch {
  const m = /^([A-Ga-g])(bb|𝄫|b|♭|#|♯|x|𝄪|##)?(-?\d+)$/.exec(s.trim());
  if (!m) throw new Error(`cannot parse pitch "${s}"`);
  const step = m[1].toUpperCase() as Step;
  const acc = m[2] ?? '';
  const alter =
    acc === 'bb' || acc === '𝄫' ? -2 : acc === 'b' || acc === '♭' ? -1 : acc === '#' || acc === '♯' ? 1 : acc === 'x' || acc === '𝄪' || acc === '##' ? 2 : 0;
  return { step, alter: alter as Alter, octave: Number(m[3]) };
}

/**
 * Spell a MIDI number on a given letter. Returns null when the needed
 * alteration would exceed a double sharp/flat.
 */
export function spellOnStep(midi: number, step: Step): Pitch | null {
  // Pick the octave whose natural step is nearest to midi.
  const base = STEP_SEMITONE[step];
  const octave = Math.round((midi - base) / 12) - 1;
  for (const o of [octave, octave - 1, octave + 1]) {
    const alter = midi - (12 * (o + 1) + base);
    if (alter >= -2 && alter <= 2) return { step, alter: alter as Alter, octave: o };
  }
  return null;
}

/** All spellings of a MIDI number with |alter| ≤ maxAlter, sorted by |alter| then sharps first. */
export function spellingsOf(midi: number, maxAlter = 2): Pitch[] {
  const out: Pitch[] = [];
  for (const step of STEPS) {
    const p = spellOnStep(midi, step);
    if (p && Math.abs(p.alter) <= maxAlter) out.push(p);
  }
  out.sort((a, b) => Math.abs(a.alter) - Math.abs(b.alter) || b.alter - a.alter);
  return out;
}

/** Line-of-fifths position of a pitch class: C=0, G=1, F=-1, F#=6, Bb=-2 … */
export function fifthsOf(p: Pick<Pitch, 'step' | 'alter'>): number {
  const BASE: Record<Step, number> = { F: -1, C: 0, G: 1, D: 2, A: 3, E: 4, B: 5 };
  return BASE[p.step] + 7 * p.alter;
}

/** Inverse of fifthsOf (alter may exceed ±2 for extreme inputs; caller should clamp). */
export function pitchClassFromFifths(k: number): { step: Step; alter: number } {
  return { step: STEPS[mod(4 * k, 7)], alter: Math.floor((k + 1) / 7) };
}
